import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import styles from '../chat.module.scss';
import { MESSAGE_STATUS, PHARMACY_SIDE } from '../utils/messageUtils';
import { formatMessageTime } from '../utils/formatMessageTime';
import StatusFlag from '../../../components/elements/StatusFlag';
import { STATUS_TONES } from '../../../shared/utils/statusTones';

const ARRIVAL_CONFIRMATION = 'arrival_confirmation';

const formatPickupWindow = (payload) => {
  const start = payload?.pickupWindowStart
    ? dayjs(payload.pickupWindowStart)
    : null;
  const end = payload?.pickupWindowEnd ? dayjs(payload.pickupWindowEnd) : null;

  const startText = start?.isValid() ? start.format('DD.MM.YYYY') : null;
  const endText = end?.isValid() ? end.format('DD.MM.YYYY') : null;

  if (startText && endText) return `${startText} – ${endText}`;
  return startText || endText || null;
};

const ClockIcon = () => (
  <svg
    className={styles.clockIcon}
    viewBox="0 0 24 24"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const FailedIcon = () => (
  <svg
    className={styles.failedIcon}
    viewBox="0 0 24 24"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5" />
    <path d="M12 16h.01" />
  </svg>
);

const MessageBubble = ({ message, onRetry }) => {
  const isOwn = message.senderSide === PHARMACY_SIDE;
  const isPending = message.status === MESSAGE_STATUS.PENDING;
  const isFailed = message.status === MESSAGE_STATUS.FAILED;

  const isArrivalConfirmation = message.actionType === ARRIVAL_CONFIRMATION;
  const isArrivalAnswered = message.actionStatus === 'answered';
  // Istekao rok za potvrdu -> korisnik nije odgovorio u naznacenom periodu.
  const isArrivalExpired = message.actionStatus === 'expired';
  const pickupWindow = isArrivalConfirmation
    ? formatPickupWindow(message.actionPayload)
    : null;

  // Status kao obojena oznaka - isto pravilo boja kao u tabelama.
  let arrivalStatusTone = STATUS_TONES.neutral;
  let arrivalStatusLabel = 'Čeka potvrdu dolaska';
  if (isArrivalAnswered) {
    arrivalStatusTone = STATUS_TONES.positive;
    arrivalStatusLabel = 'Dolazak potvrđen';
  } else if (isArrivalExpired) {
    arrivalStatusTone = STATUS_TONES.negative;
    arrivalStatusLabel = 'Korisnik nije odgovorio';
  }

  const rowClass = `${styles.row} ${isOwn ? styles['row--own'] : styles['row--other']}`;
  const bubbleClass = [
    styles.bubble,
    isOwn ? styles['bubble--own'] : styles['bubble--other'],
  ].join(' ');

  return (
    <div className={rowClass}>
      <div className={bubbleClass}>
        <div className={styles.bubble__meta}>
          <span className={styles.bubble__name}>{message.senderName}</span>
          <span>{formatMessageTime(message.createdAt)}</span>
          {/* Sat inline u meta redu: kad ack stigne i nestane, visina mehurica
              se ne menja -> nema skoka/reflow-a posle ulazne animacije. */}
          {isOwn && isPending && <ClockIcon />}
        </div>

        <div className={styles.bubble__content}>{message.content}</div>

        {isArrivalConfirmation && (
          <div className={styles.arrivalCard}>
            {pickupWindow && (
              <span className={styles.arrivalCard__window}>
                Period preuzimanja: {pickupWindow}
              </span>
            )}
            <span className={styles.arrivalCard__status}>
              <StatusFlag tone={arrivalStatusTone}>
                {arrivalStatusLabel}
              </StatusFlag>
            </span>
          </div>
        )}

        {isOwn && isFailed && (
          <span
            className={`${styles.bubble__status} ${styles['bubble__status--failed']}`}
          >
            <FailedIcon />
            Nije poslato.
            <button
              type="button"
              className={styles.retryButton}
              onClick={() => onRetry(message.clientMessageId)}
            >
              Pokušaj ponovo
            </button>
          </span>
        )}
      </div>
    </div>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    clientMessageId: PropTypes.string,
    senderSide: PropTypes.oneOf(['patient', 'pharmacy']),
    senderName: PropTypes.string,
    content: PropTypes.string,
    createdAt: PropTypes.string,
    status: PropTypes.string,
    actionType: PropTypes.string,
    actionStatus: PropTypes.oneOf(['pending', 'answered', 'expired']),
    actionPayload: PropTypes.shape({
      occurrenceId: PropTypes.string,
      pickupWindowStart: PropTypes.string,
      pickupWindowEnd: PropTypes.string,
    }),
  }).isRequired,
  onRetry: PropTypes.func.isRequired,
};

export default MessageBubble;
